<script lang="ts">
  /**
   * COLLAPSIBLE SIDEBAR
   *
   * Features:
   * - Expand/collapse toggle with smooth width transition
   * - Navigation links with active-route highlighting
   * - Role-based link visibility (Admin link only for admins)
   * - Icons always visible; text hidden when collapsed
   */
  import { link, router } from 'svelte-spa-router';
  import { authState } from '../features/auth/auth.state.svelte';

  interface Props {
    collapsed?: boolean;
  }
  let { collapsed = $bindable(false) }: Props = $props();

  interface NavItem {
    path: string;
    label: string;
    icon: string;
    adminOnly?: boolean;
  }

  const navItems: NavItem[] = [
    { path: '/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/admin', label: 'Admin Panel', icon: '🛡️', adminOnly: true },
  ];

  // Filter items based on user role
  const visibleItems = $derived(
    navItems.filter((item) => !item.adminOnly || authState.isAdmin)
  );
</script>

<aside
  class="fixed left-0 top-0 h-screen bg-surface-800 border-r border-surface-700
         flex flex-col transition-all duration-300 z-40"
  style="width: {collapsed ? '4rem' : '16rem'}"
>
  <!-- Logo / Brand -->
  <div class="h-16 flex items-center px-4 border-b border-surface-700">
    <span class="text-xl">🏢</span>
    {#if !collapsed}
      <span class="ml-3 text-lg font-bold text-surface-100 whitespace-nowrap">Dashboard</span>
    {/if}
  </div>

  <!-- Navigation Links -->
  <nav class="flex-1 py-4 px-2 flex flex-col gap-1">
    {#each visibleItems as item (item.path)}
      <a
        href={item.path}
        use:link
        class="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors duration-200
               {router.location === item.path
                 ? 'bg-brand-600/20 text-brand-400'
                 : 'text-surface-400 hover:bg-surface-700 hover:text-surface-200'}"
      >
        <span class="text-lg flex-shrink-0">{item.icon}</span>
        {#if !collapsed}
          <span class="text-sm font-medium whitespace-nowrap">{item.label}</span>
        {/if}
      </a>
    {/each}
  </nav>

  <!-- Collapse Toggle -->
  <div class="p-2 border-t border-surface-700">
    <button
      onclick={() => collapsed = !collapsed}
      class="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg
             text-surface-400 hover:bg-surface-700 hover:text-surface-200
             transition-colors cursor-pointer"
    >
      <span class="text-lg">{collapsed ? '▶' : '◀'}</span>
      {#if !collapsed}
        <span class="text-xs">Collapse</span>
      {/if}
    </button>
  </div>
</aside>
